<template>
    <div :class="['relative w-full p-1 flex justify-stretch space-x-1', border, rounded, bg]">
        <Btn
            v-for="(option, index) of options"
            :key="index"
            v-tooltip="option?.tooltip || undefined"
            active-color="primary"
            variant="tonal"
            v-bind="$attrs"
            :class="['w-full', $attrs.class]"
            :active="option.value === value"
            active-content-color="primary"
            :disabled="disabled || option?.disabled"
            :label="option.label"
            :icon="option.icon"
            :data-testid="option?.testId ?? undefined"
            :round="pill"
            @click="onClick(option)"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { BtnSwitchOption } from './BtnSwitch';

export default defineComponent({
    name: 'BtnSwitch',
    inheritAttrs: false,
    props: {
        /**
         * Selected option value attr
         */
        value: {
            type: null,
            required: true,
        },
        /**
         * List of all available options as following
         * {label: string, value: any}
         */
        options: {
            type: Array<BtnSwitchOption>,
            required: true,
            validator: (arr: [BtnSwitchOption]) =>
                arr.every((val) => ['value', 'label'].every((attr) => Object.keys(val).includes(attr))),
        },
        /**
         * Disable all inputs in the group
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether or not the switch should be without border.
         */
        noBorder: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether or not the switch should be with rounded edge.
         */
        pill: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether or not the switch should be with background color.
         */
        withBg: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input'],
    computed: {
        border(): string {
            return this.noBorder ? '' : 'border';
        },
        rounded(): string {
            return this.pill ? 'rounded-full' : 'rounded-xl';
        },
        bg(): string {
            return this.withBg ? 'bg-layout-background' : '';
        },
    },
    methods: {
        onClick(option: BtnSwitchOption) {
            if (!this.disabled) {
                this.$emit('input', option.value);
            }
        },
    },
});
</script>
